/**
 * StageMovePromptContainer — binds one press handler per valid outcome and
 * resolves the reason-field gate for `StageMovePromptView` (SE-5495, SE-5496).
 *
 * Both used to live in the View: a `useMemo` building the handler record, and
 * the `showReason` derivation after an early return. A View is a pure function
 * of its props, so the binding and the gate belong here (SE-8019).
 * @module features/player-kanban/components/molecules/StageMovePrompt/StageMovePromptContainer
 */

import { useMemo } from "react";

import StageMovePromptView from "./StageMovePromptView";

import type { KanbanCardOutcome } from "@/generated/graphql";

/**
 * Props for {@link StageMovePromptContainer} — supplied verbatim by
 * `useStageMovePrompt`.
 */
export interface StageMovePromptContainerProps {
  /** Whether the prompt is visible. */
  readonly isOpen: boolean;
  /** Player being moved. */
  readonly playerName?: string;
  /** Destination stage name. */
  readonly toPhaseName?: string;
  /** Origin stage name. */
  readonly fromPhaseName?: string;
  /** Whether a free-text reason is required. */
  readonly requiresRationale: boolean;
  /** Whether a terminal outcome is required (SE-5496). */
  readonly requiresOutcome: boolean;
  /** SE-5496: the board template's ordered valid outcomes to choose from. */
  readonly validOutcomes: readonly KanbanCardOutcome[];
  /** SE-5496: the currently-selected outcome, if any. */
  readonly selectedOutcome?: KanbanCardOutcome;
  /** SE-5496: outcome selection handler. */
  readonly onSelectOutcome: (outcome: KanbanCardOutcome) => void;
  /** Current reason text. */
  readonly rationale: string;
  /** Reason text change handler. */
  readonly onChangeRationale: (value: string) => void;
  /** Whether confirm is blocked by an unmet gate. */
  readonly isSubmitDisabled: boolean;
  /** Confirm handler. */
  readonly onSubmit: () => void;
  /** Cancel handler — abandons the pending move. */
  readonly onCancel: () => void;
}

/**
 * Resolve the per-outcome handlers and the reason gate, then render the prompt.
 * @param props - Container props (see {@link StageMovePromptContainerProps})
 * @param props.isOpen - Whether the prompt is visible
 * @param props.playerName - Player being moved
 * @param props.toPhaseName - Destination stage name
 * @param props.fromPhaseName - Origin stage name
 * @param props.requiresRationale - Whether a reason is required
 * @param props.requiresOutcome - Whether an outcome is required (SE-5496)
 * @param props.validOutcomes - Ordered valid outcomes for the selector
 * @param props.selectedOutcome - Currently-selected outcome
 * @param props.onSelectOutcome - Outcome selection handler
 * @param props.rationale - Current reason text
 * @param props.onChangeRationale - Reason text change handler
 * @param props.isSubmitDisabled - Whether confirm is blocked
 * @param props.onSubmit - Confirm handler
 * @param props.onCancel - Cancel handler
 * @returns The bound prompt.
 */
function StageMovePromptContainer({
  isOpen,
  playerName,
  toPhaseName,
  fromPhaseName,
  requiresRationale,
  requiresOutcome,
  validOutcomes,
  selectedOutcome,
  onSelectOutcome,
  rationale,
  onChangeRationale,
  isSubmitDisabled,
  onSubmit,
  onCancel,
}: StageMovePromptContainerProps) {
  // Pre-bind a stable press handler per outcome so the View passes a plain
  // reference (react-perf/jsx-no-new-function-as-prop forbids inline closures in
  // attributes) — the repo's standard handler-record pattern for list rows.
  const outcomeHandlers = useMemo(
    () =>
      Object.fromEntries(
        validOutcomes.map(outcome => [outcome, () => onSelectOutcome(outcome)])
      ) as Record<KanbanCardOutcome, () => void>,
    [validOutcomes, onSelectOutcome]
  );

  // SE-5496: a terminal close requires a reason as well, so the reason field is
  // shown whenever a rationale OR an outcome is required.
  const showReason = requiresRationale || requiresOutcome;

  return (
    <StageMovePromptView
      isOpen={isOpen}
      playerName={playerName}
      toPhaseName={toPhaseName}
      fromPhaseName={fromPhaseName}
      requiresOutcome={requiresOutcome}
      validOutcomes={validOutcomes}
      selectedOutcome={selectedOutcome}
      outcomeHandlers={outcomeHandlers}
      rationale={rationale}
      onChangeRationale={onChangeRationale}
      isSubmitDisabled={isSubmitDisabled}
      showReason={showReason}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  );
}

export default StageMovePromptContainer;
